import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig
} from "axios";
import { tokenStorage } from "@repo/lib/auth/token-storage";

type RefreshHandler = () => Promise<void>;

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _retryCount?: number;
}

type AuthFailureHandler = () => void;

let refreshHandler: RefreshHandler | null = null;
let authFailureHandler: AuthFailureHandler | null = null;
let isRefreshing = false;
let pendingQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
  config: RetryableRequestConfig;
}> = [];

export const resolveApiBaseUrl = () => {
  const viteEnv =
    typeof import.meta !== "undefined"
      ? ((import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? undefined)
      : undefined;
  if (viteEnv?.VITE_PUBLIC_API_URL !== undefined) {
    return viteEnv.VITE_PUBLIC_API_URL;
  }
  if (typeof process !== "undefined") {
    if (process.env.EXPO_PUBLIC_API_URL !== undefined) return process.env.EXPO_PUBLIC_API_URL;
  }
  if (typeof window !== "undefined") {
    const fromWindow = (window as unknown as Record<string, string>)["__API_URL__"];
    if (fromWindow !== undefined) return fromWindow;
  }
  return "";
};

const client: AxiosInstance = axios.create({
  baseURL: resolveApiBaseUrl(),
  withCredentials: true,
  timeout: 15000
});

client.interceptors.request.use((config: RetryableRequestConfig) => {
  config.headers = config.headers ?? {};
  // Don't clobber an Authorization header the caller already set explicitly (e.g. tenant-portal
  // customer-token calls, or apiClient.defaults.headers.common.Authorization set from a customer
  // authToken) — otherwise a staff/admin session token sitting in tokenStorage on the same
  // origin silently overrides the customer token and every customer-authenticated call 401s.
  if (!config.headers.Authorization) {
    const token = tokenStorage.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

const processQueue = (error: unknown, token?: string) => {
  pendingQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
      return;
    }
    if (token) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    resolve(client(config));
  });
  pendingQueue = [];
};

client.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (!error.config) {
      return Promise.reject(error);
    }

    const originalRequest = error.config as RetryableRequestConfig;
    const status = error.response?.status;

    // Don't attempt to refresh token for auth endpoints or if already retried
    // Extract server error message so error.message contains the backend message
    const responseData = error.response?.data as Record<string, unknown> | undefined;
    const serverMessage = responseData?.message;
    // 若有 express-validator errors 陣列，提取具體欄位錯誤訊息
    const validationErrors = responseData?.errors;
    if (Array.isArray(validationErrors) && validationErrors.length > 0) {
      const details = validationErrors
        .map((e: any) => e.msg)
        .filter((m: any) => typeof m === "string" && m)
        .join("；");
      if (details) {
        error.message = details;
      } else if (typeof serverMessage === "string" && serverMessage) {
        error.message = serverMessage;
      }
    } else if (typeof serverMessage === "string" && serverMessage) {
      error.message = serverMessage;
    }

    // 429 Too Many Requests — 自動重試，最多 3 次，指數退避
    if (status === 429) {
      const retryCount = originalRequest._retryCount ?? 0;
      if (retryCount < 3) {
        originalRequest._retryCount = retryCount + 1;
        const retryAfter = error.response?.headers?.["retry-after"];
        const delay = retryAfter
          ? parseInt(retryAfter, 10) * 1000
          : Math.min(1000 * 2 ** retryCount, 8000);
        await new Promise((r) => setTimeout(r, delay));
        return client(originalRequest);
      }
      error.message = "請求過於頻繁，請稍後再試";
      return Promise.reject(error);
    }

    if (
      status !== 401 ||
      originalRequest._retry ||
      (originalRequest.url && (
        originalRequest.url.includes("/auth/refresh") ||
        originalRequest.url.includes("/auth/login") ||
        originalRequest.url.includes("/auth/register")
      ))
    ) {
      return Promise.reject(error);
    }

    if (!refreshHandler) {
      await tokenStorage.clearTokens();
      authFailureHandler?.();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({ resolve, reject, config: originalRequest });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      await refreshHandler();
      processQueue(null, tokenStorage.getAccessToken() ?? undefined);
      return client(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);
      await tokenStorage.clearTokens();
      authFailureHandler?.();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export const setRefreshHandler = (handler: RefreshHandler | null) => {
  refreshHandler = handler;
};

export const setAuthFailureHandler = (handler: AuthFailureHandler | null) => {
  authFailureHandler = handler;
};

export const apiClient = client;
